
import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { AppBar, Box, Button, CssBaseline, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, Tooltip, CircularProgress, Breadcrumbs } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { app } from '../../firebase';
import { useTranslation } from 'react-i18next';
import HelpSidebar from './HelpSidebar';
import { getHelpContent } from '../../services/helpContentService';
import { useUserRoles } from '../../hooks/useUserRoles';
import { menuItems } from '../../constants';
import LanguageSelector from '../shared/LanguageSelector'; 

const drawerWidth = 240;
const collapsedDrawerWidth = 65;
const helpSidebarWidth = 360; 
const auth = getAuth(app);

export default function Layout({ children }: { children: React.ReactNode }) {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [helpSidebarOpen, setHelpSidebarOpen] = useState(false);
    const [dynamicTitles, setDynamicTitles] = useState<Record<string, { text: string; icon?: React.ReactNode }>>({});
    const { isSuperAdmin, navigationModules, navigationSubModules, isRolesLoading } = useUserRoles();

    const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
        window.dispatchEvent(new CustomEvent('app-drawer-toggle'));
    };
    const handleLogout = () => signOut(auth).then(() => navigate('/login'));
    const handleHelpSidebarToggle = () => setHelpSidebarOpen(!helpSidebarOpen);
    
    const currentPageHelpContent = useMemo(() => {
        return getHelpContent(location.pathname, i18n.language);
    }, [location.pathname, i18n.language]);

    useEffect(() => {
        const handleUpdate = (event: any) => {
            const { path, text, icon } = event.detail;
            setDynamicTitles(prev => {
                if (!text) {
                    const next = { ...prev };
                    delete next[path];
                    return next;
                }
                return { ...prev, [path]: { text, icon } };
            });
        };
        window.addEventListener('setBreadcrumbTitle', handleUpdate);
        return () => window.removeEventListener('setBreadcrumbTitle', handleUpdate);
    }, []);
    


    const breadcrumbs = useMemo(() => {
        const parts = location.pathname.split('/').filter(Boolean);
        const result = [];
        let currentPath = '';

        for (let i = 0; i < parts.length; i++) {
            currentPath += `/${parts[i]}`;
            
            // Try to find in top level
            let item = menuItems.find(m => m.path === currentPath);
            
            // If not found, try to find in subItems of the previous level
            if (!item && i > 0) {
                const parentPath = `/${parts[i-1]}`;
                const parent = menuItems.find(m => m.path.startsWith(parentPath));
                if (parent?.subItems) {
                    item = parent.subItems.find(s => s.path === currentPath) as any;
                }
            }

            if (item) {
                result.push({
                    text: item.text,
                    path: item.path,
                    isLast: i === parts.length - 1
                });
            } else {
                // Check if we have a dynamic override for this segment (could be intermediate or last)
                const override = dynamicTitles[currentPath];
                if (override) {
                    result.push({
                        text: override.text,
                        icon: override.icon,
                        path: currentPath,
                        isLast: i === parts.length - 1
                    });
                } else if (i === parts.length - 1) {
                    // Fallback for the last segment if no override and not in menu
                    const fallbackText =
                        currentPath === '/profile'
                            ? 'User Profile'
                            : parts[i].split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    result.push({
                        text: fallbackText,
                        path: currentPath,
                        isLast: true
                    });
                }
            }
        }

        // If at root or dashboard, just show Dashboard
        if (result.length === 0 || (result.length === 1 && result[0].path === '/dashboard')) {
            return [{ text: 'Dashboard', path: '/dashboard', isLast: true }];
        }

        return result;
    }, [location.pathname, dynamicTitles]);

    const visibleMenuItems = useMemo(() => {
        if (isRolesLoading) {
            return [];
        }

        // Super Admin bypass to show everything
        if (isSuperAdmin) {
            return menuItems;
        }

        // Filter menu items strictly by the navigationModules assigned to the user
        // (The useUserRoles hook handles defaults/super-admin logic)
        return menuItems
            .filter(item => navigationModules?.includes(item.navId))
            .map(item => {
                if (item.subItems) {
                    const visibleSubItems = item.subItems.filter(sub => sub.navId && navigationSubModules?.includes(sub.navId));
                    return { ...item, subItems: visibleSubItems };
                }
                return item;
            });
    }, [isSuperAdmin, navigationModules, navigationSubModules, isRolesLoading]);

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between', px: [1], py: [2] }}>
                 {!isCollapsed && <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', ml: 1 }}>Menu</Typography>}
                 <IconButton onClick={() => setIsCollapsed(!isCollapsed)} sx={{ color: 'white' }}>
                    {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                 </IconButton>
            </Toolbar>
            {isRolesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                    <CircularProgress color="inherit" />
                </Box>
            ) : (
                <List sx={{ 
                    flexGrow: 1, 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px' }
                }}>
                    {visibleMenuItems.map((item) => (
                        <Tooltip key={item.text} title={isCollapsed ? item.text : ""} placement="right" arrow disableHoverListener={!isCollapsed}>
                            <ListItemButton 
                                component={Link} 
                                to={item.path} 
                                onClick={() => setMobileOpen(false)}
                                selected={location.pathname.startsWith(item.path) && item.path !== '/'} 
                                sx={{
                                    minHeight: 48,
                                    justifyContent: isCollapsed ? 'center' : 'initial',
                                    px: 2.5,
                                    '&.Mui-selected': { 
                                        backgroundColor: '#08BED5', 
                                        color: 'white', 
                                        '& .MuiListItemIcon-root': { color: 'white' } 
                                    }
                                }}
                            >
                                <ListItemIcon 
                                    sx={{
                                        minWidth: 0,
                                        mr: isCollapsed ? 0 : 3,
                                        justifyContent: 'center',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        transition: (theme) => theme.transitions.create('margin', {
                                            easing: theme.transitions.easing.sharp,
                                            duration: theme.transitions.duration.shorter,
                                        }),
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text} 
                                    sx={{ 
                                        opacity: isCollapsed ? 0 : 1,
                                        transition: (theme) => theme.transitions.create('opacity', {
                                            easing: theme.transitions.easing.sharp,
                                            duration: theme.transitions.duration.shorter,
                                        }),
                                        '& .MuiListItemText-primary': {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden'
                                        }
                                    }} 
                                />
                            </ListItemButton>
                        </Tooltip>
                    ))}
                </List>
            )}
            <Box sx={{ p: 2 }}>
                <Box sx={{ 
                    mb: 2, 
                    opacity: isCollapsed ? 0 : 1, 
                    height: isCollapsed ? 0 : 'auto', 
                    overflow: 'hidden',
                    transition: (theme) => theme.transitions.create(['opacity', 'height'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter,
                    }),
                }}>
                    <LanguageSelector />
                </Box>
                <Tooltip title={isCollapsed ? "Profile" : ""} placement="right" arrow disableHoverListener={!isCollapsed}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate('/profile')}
                        sx={{
                            mb: 1,
                            bgcolor: 'rgba(0,0,0,0.2)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' },
                            minWidth: isCollapsed ? '40px' : 'auto',
                            px: isCollapsed ? 1 : 2,
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            transition: (theme) => theme.transitions.create(['width', 'padding', 'background-color'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.shorter,
                            }),
                        }}
                    >
                        <AccountCircleIcon fontSize="small" sx={{ mr: isCollapsed ? 0 : 1 }} />
                        <Box component="span" sx={{
                            opacity: isCollapsed ? 0 : 1,
                            display: isCollapsed ? 'none' : 'inline',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>
                            Profile
                        </Box>
                    </Button>
                </Tooltip>
                <Tooltip title={isCollapsed ? "Logout" : ""} placement="right" arrow disableHoverListener={!isCollapsed}>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleLogout} 
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.2)', 
                            '&:hover': {bgcolor: 'rgba(0,0,0,0.4)'},
                            minWidth: isCollapsed ? '40px' : 'auto',
                            px: isCollapsed ? 1 : 2,
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            transition: (theme) => theme.transitions.create(['width', 'padding', 'background-color'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.shorter,
                            }),
                        }}
                    >
                        <LogoutIcon fontSize="small" sx={{ mr: isCollapsed ? 0 : 1 }} />
                        <Box component="span" sx={{ 
                            opacity: isCollapsed ? 0 : 1, 
                            display: isCollapsed ? 'none' : 'inline',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>
                            Logout
                        </Box>
                    </Button>
                </Tooltip>
            </Box>
        </Box>
    );

     return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <CssBaseline />
                <AppBar 
                    position="fixed" 
                    sx={{ 
                        width: '100%', 
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        transition: (theme) => theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        backgroundColor: 'white',
                        color: 'text.primary',
                        boxShadow: 'none',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Toolbar disableGutters>
                        <IconButton 
                            color="inherit" 
                            aria-label="open drawer" 
                            edge="start" 
                            onClick={handleDrawerToggle} 
                            sx={{ 
                                ml: 1, 
                                mr: 1, 
                                display: { xs: 'inline-flex', sm: 'none' } 
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box sx={{ 
                            width: { xs: 'auto', sm: drawerWidth }, 
                            display: 'flex', 
                            justifyContent: { xs: 'flex-start', sm: 'center' }, 
                            alignItems: 'center',
                            flexShrink: 0,
                        }}>
                             <Box 
                                 component="img"
                                 src="/assets/images/TD-SYNNEX-Synapse.png" 
                                 alt="SYNapse Logo" 
                                 sx={{ 
                                     height: { xs: '22px', sm: '30px' }, 
                                     maxWidth: '100%',
                                     objectFit: 'contain' 
                                 }} 
                             />
                        </Box>
                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', ml: { xs: 1.5, sm: 4 }, minWidth: 0 }}>
                            {/* Mobile-only page title */}
                            {breadcrumbs[breadcrumbs.length - 1] && (
                                <Typography
                                    variant="h6"
                                    color="text.primary"
                                    sx={{
                                        fontWeight: 500,
                                        display: { xs: 'block', sm: 'none' },
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: '100%'
                                    }}
                                >
                                    {breadcrumbs[breadcrumbs.length - 1].text}
                                </Typography>
                            )}

                            {/* Tablet/Desktop breadcrumbs */}
                            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '100%', minWidth: 0 }}>
                                <Breadcrumbs 
                                    separator={<NavigateNextIcon fontSize="small" />} 
                                    aria-label="breadcrumb"
                                    sx={{ 
                                        color: 'text.primary',
                                        '& ol': {
                                            p: 0,
                                            m: 0,
                                            listStyle: 'none !important',
                                            flexWrap: 'nowrap',
                                            overflow: 'hidden',
                                        },
                                        '& li': {
                                            p: 0,
                                            m: 0,
                                            listStyle: 'none !important',
                                            '&::before': { display: 'none !important' },
                                            '&:last-of-type': {
                                                minWidth: 0,
                                                overflow: 'hidden',
                                            },
                                        },
                                        '& .MuiBreadcrumbs-separator svg': {
                                            fontSize: { xs: '14px', sm: '18px' }
                                        }
                                    }}
                                >
                                    {breadcrumbs.map((b, index) => (
                                        b.isLast ? (
                                            <Typography 
                                                key={index} 
                                                variant="h6" 
                                                color="text.primary" 
                                                noWrap
                                                title={b.text}
                                                sx={{ 
                                                    fontWeight: 500, 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    fontSize: { xs: '0.85rem', sm: '1rem' },
                                                    lineHeight: 1,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {b.icon && (
                                                    <Box component="span" sx={{ 
                                                        mr: 1, 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        flexShrink: 0,
                                                        '& svg, & img': { 
                                                            fontSize: { xs: '16px !important', sm: '18px !important' }, 
                                                            width: { xs: '16px !important', sm: '18px !important' }, 
                                                            height: { xs: '16px !important', sm: '18px !important' },
                                                            filter: 'grayscale(100%) opacity(0.9)',
                                                            color: 'inherit !important'
                                                        }
                                                    }}>
                                                        {b.icon}
                                                    </Box>
                                                )}
                                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {b.text}
                                                </Box>
                                            </Typography>
                                        ) : (
                                            <Typography
                                                key={index}
                                                component={Link}
                                                to={b.path}
                                                variant="h6"
                                                sx={{
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    fontSize: { xs: '0.85rem', sm: '1rem' },
                                                    lineHeight: 1,
                                                    fontWeight: 400,
                                                    opacity: 0.7,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {b.icon && (
                                                    <Box component="span" sx={{ 
                                                        mr: 1, 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        '& svg, & img': { 
                                                            fontSize: { xs: '16px !important', sm: '18px !important' }, 
                                                            width: { xs: '16px !important', sm: '18px !important' }, 
                                                            height: { xs: '16px !important', sm: '18px !important' },
                                                            filter: 'grayscale(100%)',
                                                            color: 'inherit !important'
                                                        }
                                                    }}>
                                                        {b.icon}
                                                    </Box>
                                                )}
                                                {b.text}
                                            </Typography>
                                        )
                                    ))}
                                </Breadcrumbs>
                            </Box>
                        </Box>
                        <Box sx={{ px: 2 }}>
                            <Tooltip title="Help">
                                <IconButton color="inherit" onClick={handleHelpSidebarToggle}>
                                    <HelpOutlineIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Box component="nav" sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 }, transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }) }}>
                    <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>
                        {drawer}
                    </Drawer>
                    <Drawer 
                        variant="permanent" 
                        sx={{ 
                            display: { xs: 'none', sm: 'block' }, 
                            '& .MuiDrawer-paper': { 
                                boxSizing: 'border-box', 
                                width: currentDrawerWidth,
                                overflowX: 'hidden',
                                transition: (theme) => theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }),
                                borderRight: 'none',
                                mt: '64px',
                                height: 'calc(100% - 64px)'
                            } 
                        }} 
                        open
                    >
                        {drawer}
                    </Drawer>
                </Box>
                <Box 
                    component="main" 
                    sx={{ 
                        flexGrow: 1, 
                        p: 3, 
                        width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                        transition: (theme) => theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    }}
                >
                    <Toolbar />
                    {children}
                </Box>
                <HelpSidebar isOpen={helpSidebarOpen} onClose={handleHelpSidebarToggle} content={currentPageHelpContent} width={helpSidebarWidth} />
            </Box>
            <Box 
                component="footer"
                sx={{
                    p: 2,
                    mt: 'auto',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    ml: { sm: `${currentDrawerWidth}px` },
                    textAlign: 'center',
                    transition: (theme) => theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    © {new Date().getFullYear()} TD SYNNEX
                    {' | '}
                    <Link to="/privacy-policy" style={{ textDecoration: 'none', color: 'inherit' }}>
                        Privacy Policy
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
}
